/**
 * ContextPanel - Slide-in side panel for contextual details
 *
 * Used to display legal quotes, entity details, document previews, etc.
 * without navigating away from the current page context.
 */

import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './ContextPanel.css';

export type ContextPanelWidth = 'narrow' | 'medium' | 'wide';

export interface ContextPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;

  /** Callback when panel should close */
  onClose: () => void;

  /** Panel title */
  title: string;

  /** Optional subtitle */
  subtitle?: string;

  /** Panel width variant */
  width?: ContextPanelWidth;

  /** Panel content */
  children: React.ReactNode;

  /** Whether to show overlay backdrop */
  showOverlay?: boolean;

  /**
   * Consumer controls on the title row — between the title and the close button — so the
   * first row of the scrolling content is not spent on them (2026-09-07). The header centres
   * its items and lets the title shrink to its content when a slot is present.
   */
  headerSlot?: React.ReactNode;

  /**
   * Consumer actions pinned at the panel bottom (FR-018.7), outside the scrolling content.
   * Rendered only when provided; laid out right-aligned.
   */
  footerSlot?: React.ReactNode;

  /** Additional CSS class */
  className?: string;
}

export const ContextPanel: React.FC<ContextPanelProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 'medium',
  children,
  showOverlay = true,
  headerSlot,
  footerSlot,
  className = '',
}) => {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Add/remove escape key listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const panelContent = (
    <div className={`context-panel-container ${isOpen ? 'context-panel-container--open' : ''}`}>
      {showOverlay && (
        <div className="context-panel__overlay" onClick={onClose} aria-hidden="true" />
      )}
      <aside
        className={`context-panel context-panel--${width} ${className}`}
        role="complementary"
        aria-label={title}
      >
        <header className={`context-panel__header${headerSlot ? ' context-panel__header--with-slot' : ''}`}>
          <div className="context-panel__title-group">
            <h2 className="context-panel__title">{title}</h2>
            {subtitle && <p className="context-panel__subtitle">{subtitle}</p>}
          </div>
          {headerSlot && <div className="context-panel__header-slot">{headerSlot}</div>}
          <button
            type="button"
            className="context-panel__close"
            onClick={onClose}
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </header>
        <div className="context-panel__content">{children}</div>
        {footerSlot && <div className="context-panel__footer">{footerSlot}</div>}
      </aside>
    </div>
  );

  // Render via portal to ensure proper z-index stacking
  return createPortal(panelContent, document.body);
};

export default ContextPanel;
